import {ReactNode, useState} from "react";
import {CropPortrait, Dashboard, DoorFront, GridOn, PinDrop, Toc, VerticalAlignTop, ZoomOutMap} from "@mui/icons-material";
import {ElevationApp} from "../ElevationApp";
import {MdDisplayView, workspaceViews} from "../model/view/MdViewSettings";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {PropertiesDialog} from "../shared-2d/Properties";
import {RibbonButton, RibbonCheckbox, RibbonGroup, RibbonStack} from "./Ribbon";

function getViews(): {value: MdDisplayView; label: string; icon: ReactNode}[] {
    return [
        {value: "all", label: "All", icon: <Dashboard />},
        {value: "door-active", label: "Door", icon: <DoorFront />},
        {value: "location-active", label: "Locations", icon: <PinDrop />},
        {value: "frame-grid", label: "Frame Grid", icon: <GridOn />},
        {value: "frame-profile-active", label: "Frame Profile", icon: <CropPortrait />},
        {value: "top-down", label: "Top Down", icon: <VerticalAlignTop />},
    ]
}

// the view-picking groups at the start of a workspace's ribbon tab (see WorkspaceTab):
// the Display views the workspace offers (omitted when it has only one) and, on a pair,
// the leaf the view shows.
export function DisplayGroups(props: {
    app: ElevationApp;
}) {
    const activeView = useEventEmitter2(props.app.viewSettings.activeView);
    const workspace = useEventEmitter2(props.app.viewSettings.workspace);

    const allowedViews = workspaceViews(workspace ?? "screen");

    const isFrameProfileView = activeView === "frame-profile-active" || activeView === "frame-profile-inactive";
    const isDoorView = activeView === "door-active" || activeView === "door-inactive";
    const isLocationView = activeView === "location-active" || activeView === "location-inactive";

    return (
        <>
            {allowedViews.length > 1 && (
                <RibbonGroup label="Display">
                    {getViews().filter(v => allowedViews.includes(v.value)).map(v => (
                        <RibbonButton
                            key={v.value}
                            icon={v.icon}
                            label={v.label}
                            active={activeView === v.value}
                            onClick={() => props.app.showView(v.value)}
                        />
                    ))}
                </RibbonGroup>
            )}

            {isDoorView && props.app.frameSpec.isPair && (
                <RibbonGroup label="Leaf">
                    <RibbonButton
                        icon={<DoorFront />}
                        label="Active"
                        active={activeView === "door-active"}
                        onClick={() => props.app.showView("door-active")}
                    />
                    <RibbonButton
                        icon={<DoorFront />}
                        label="Inactive"
                        active={activeView === "door-inactive"}
                        onClick={() => props.app.showView("door-inactive")}
                    />
                </RibbonGroup>
            )}

            {isLocationView && props.app.frameSpec.isPair && (
                <RibbonGroup label="Leaf">
                    <RibbonButton
                        icon={<PinDrop />}
                        label="Active"
                        active={activeView === "location-active"}
                        onClick={() => props.app.showView("location-active")}
                    />
                    <RibbonButton
                        icon={<PinDrop />}
                        label="Inactive"
                        active={activeView === "location-inactive"}
                        onClick={() => props.app.showView("location-inactive")}
                    />
                </RibbonGroup>
            )}

            {isFrameProfileView && props.app.frameSpec.isPair && (
                <RibbonGroup label="Leaf">
                    <RibbonButton
                        icon={<CropPortrait />}
                        label="Active"
                        active={activeView === "frame-profile-active"}
                        onClick={() => props.app.showView("frame-profile-active")}
                    />
                    <RibbonButton
                        icon={<CropPortrait />}
                        label="Inactive"
                        active={activeView === "frame-profile-inactive"}
                        onClick={() => props.app.showView("frame-profile-inactive")}
                    />
                </RibbonGroup>
            )}
        </>
    );
}

// the view-option groups at the end of a workspace's ribbon tab (see WorkspaceTab):
// zoom, the properties dialog and the dimension toggles.
export function ViewOptionGroups(props: {
    app: ElevationApp;
}) {
    const activeView = useEventEmitter2(props.app.viewSettings.activeView);
    const dimShowAll = useEventEmitter2(props.app.viewSettings.dimShowAll);
    const dimSimple = useEventEmitter2(props.app.viewSettings.dimSimple);
    const showAnchors = useEventEmitter2(props.app.viewSettings.showAnchors);
    const [showProperties, setShowProperties] = useState(false);

    const isFrameProfileView = activeView === "frame-profile-active" || activeView === "frame-profile-inactive";

    // the frame-profile / top-down views used to carry their own on-canvas options
    // menu (simple-dimensions toggle + properties); those controls now live here and
    // only apply while one of those views is active.
    const usesViewOptions = isFrameProfileView || activeView === "top-down";

    return (
        <>
            <RibbonGroup label="View">
                <RibbonButton
                    icon={<ZoomOutMap />}
                    label="Reset Zoom"
                    onClick={() => {
                        props.app.draw.resetView();
                        props.app.draw.currentScene?.applyLayout();
                    }}
                />
                {usesViewOptions && (
                    <RibbonButton
                        icon={<Toc />}
                        label="Properties"
                        onClick={() => setShowProperties(true)}
                    />
                )}
            </RibbonGroup>

            <RibbonGroup label="Dimensions">
                <RibbonStack>
                    <RibbonCheckbox
                        label="Show All Dimensions"
                        checked={!!dimShowAll}
                        onChange={checked => props.app.viewSettings.dimShowAll.emit(checked)}
                    />
                    {(activeView === "frame-grid" || activeView === "all") && (
                        <RibbonCheckbox
                            label="Show Anchors"
                            checked={!!showAnchors}
                            onChange={checked => props.app.viewSettings.showAnchors.emit(checked)}
                        />
                    )}
                    {usesViewOptions && (
                        <RibbonCheckbox
                            label="Simple Dimensions"
                            checked={!!dimSimple}
                            onChange={checked => props.app.viewSettings.dimSimple.emit(checked)}
                        />
                    )}
                </RibbonStack>
            </RibbonGroup>

            {showProperties && (
                <PropertiesDialog app={props.app} onClose={() => setShowProperties(false)} />
            )}
        </>
    );
}
